import React, { useState, useEffect, useRef } from 'react';
import { putApi } from '../../../Services/apiService';
import { GoogleMap, Marker, useLoadScript, Autocomplete } from '@react-google-maps/api';

const EditVenueModal = ({ setEditModal, venueDetails, fetchVenues }) => {
    const [loader, setLoader] = useState(false);
    const [errors, setErrors] = useState({});
    const [deleteImages, setDeleteImages] = useState([]);
    const [deleteDishes, setDeleteDishes] = useState([]);
    const [deleteCategoryTypes, setDeleteCategoryTypes] = useState([]);
    const [price, setPrice] = useState('');
    const [filePreviews, setFilePreviews] = useState({
        banner_image: null,
        images: []
    });

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyAdn5bqjKHkbI5qkM2VJre9POdgo513Hxg",
        libraries: ['places']
    });

    // Price level mapping
    const priceRangeMapping = {
        '$': 1,
        '$$': 2,
        '$$$': 3,
        '$$$$': 4,
        '$$$$$': 5
    };

    const getPriceSymbol = (numericValue) => {
        const num = Number(numericValue);
        return Object.keys(priceRangeMapping).find(key => priceRangeMapping[key] === num) || '$';
    };

    const [formData, setFormData] = useState({
        name: "",
        website_link: "",
        phone_number: "",
        email: "",
        wishlist_booking_url: "",
        category_type: [],
        price_range: "",
        type: "",
        latitude: "",
        longitude: "",
        location: "",
        pincode: "",
        short_desc: "",
        description: "",
        banner_image: null,
        images: [],
        dishes: [],
        country: "",
        city: ""
    });

    useEffect(() => {
        if (venueDetails) {
            const priceSymbol = getPriceSymbol(venueDetails.price_range);
            setPrice(priceSymbol);
    
            const categoryNames = (venueDetails.Category_Types || []).map(ct => ct.name);
            const dishNames = (venueDetails.Category_Dishes || []).map(dish => ({
                id: dish.id,
                name: dish.name
            }));
    
            setFormData({
                ...venueDetails,
                price_range: venueDetails.price_range || 1,
                images: venueDetails.Category_Images || [],
                banner_image: venueDetails.banner_image || null,
                category_type: categoryNames,
                dishes: dishNames
            });
    
            setFilePreviews({
                banner_image: venueDetails.banner_image || null,
                images: venueDetails.Category_Images || []
            });
        }
    }, [venueDetails]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (name === 'banner_image') {
            const file = files[0];
            setFormData(prev => ({ ...prev, banner_image: file }));
            setFilePreviews(prev => ({
                ...prev,
                banner_image: URL.createObjectURL(file)
            }));
            if (errors.banner_image) {
                setErrors({ ...errors, banner_image: '' });
            }
        } else if (name === 'images') {
            const filesArray = Array.from(files);
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...filesArray]
            }));
            const previewsArray = filesArray.map(file => URL.createObjectURL(file));
            setFilePreviews(prev => ({
                ...prev,
                images: [...prev.images, ...previewsArray]
            }));
            if (errors.images) {
                setErrors({ ...errors, images: '' });
            }
        }
    };

    const handlePriceRangeChange = (priceSymbol) => {
        const priceValue = priceRangeMapping[priceSymbol];
        setFormData({ ...formData, price_range: priceValue });
        setPrice(priceSymbol);
        if (errors.price_range) {
            setErrors({ ...errors, price_range: '' });
        }
    };

    const removeBannerImage = () => {
        setFormData(prev => ({ ...prev, banner_image: null }));
        setFilePreviews(prev => ({ ...prev, banner_image: null }));
        setErrors(prev => ({ ...prev, banner_image: "Banner image is required." }));
    };

    const removeImage = (index) => {
        setFormData(prev => {
            const removedImage = prev.images[index];
            if (removedImage.url) {
                setDeleteImages(prevDelete => [...prevDelete, removedImage.id]);
            }
            return {
                ...prev,
                images: prev.images.filter((_, i) => i !== index)
            };
        });

        setFilePreviews(prev => {
            const updatedImages = [...prev.images];
            updatedImages.splice(index, 1);
            return {
                ...prev,
                images: updatedImages
            };
        });
    };

    const handleDishRemove = (dishId) => {
        setDeleteDishes(prev => [...prev, dishId]);
        setFormData(prev => ({
            ...prev,
            dishes: prev.dishes.filter(dish => dish.id !== dishId)
        }));
    };

    const handleCategoryTypeRemove = (categoryType) => {
        setDeleteCategoryTypes(prev => [...prev, categoryType]);
        setFormData(prev => ({
            ...prev,
            category_type: prev.category_type.filter(ct => ct !== categoryType)
        }));
    };

    const validateForm = () => {
        let validationErrors = {};

        if (!formData.name.trim()) validationErrors.name = "Name is required.";
        // if (!formData.description.trim()) validationErrors.description = "Description is required.";
        if (formData.wishlist_booking_url?.trim()) {
            const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/;
            if (!urlPattern.test(formData.wishlist_booking_url.trim())) {
              validationErrors.wishlist_booking_url = "Enter a valid URL starting with http:// or https://";
            }
          }
         
          if (formData.website_link?.trim()) {
            const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/;
            if (!urlPattern.test(formData.website_link.trim())) {
              validationErrors.website_link = "Enter a valid URL starting with http:// or https://";
            }
          }
          if (formData.description && formData.description.trim() !== "") {
            if (formData.description.trim().length < 2 || formData.description.trim().length > 500) {
              validationErrors.description = "Description must be between 2 and 500 characters.";
          }
          }
          
          // Validate short description: not required, but if provided must be between 2 and 250 characters
          if (formData.short_desc && formData.short_desc.trim() !== "") {
            if (formData.short_desc.trim().length < 2 || formData.short_desc.trim().length > 250) {
              validationErrors.short_desc = "Short description must be between 2 and 250 characters.";
            }
          }
        // if (!formData.phone_number.trim()) validationErrors.phone_number = "Phone number is required.";
        if (!formData.banner_image && !filePreviews.banner_image) validationErrors.banner_image = "Banner image is required.";
        if (formData.images.length === 0 && filePreviews.images.length === 0) validationErrors.images = "At least one image is required.";
        if (!formData.type) validationErrors.type = "Please select a venue type.";
        if (!formData.price_range) validationErrors.price_range = "Please select a price range.";
        // if (!formData.category_type || formData.category_type.length === 0) validationErrors.category_type = "Please select a category type.";

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };
    const handleSubmit = async () => {
        if (!validateForm()) return;
    
        setLoader(true);
        const formDataToSend = new FormData();
    
        // Handle banner image
        if (formData.banner_image instanceof File) {
            formDataToSend.append("banner_image", formData.banner_image);
        } else if (filePreviews.banner_image) {
            formDataToSend.append("existingBannerImage", filePreviews.banner_image);
        }
    
        // Handle images - new files and existing URLs
        formData.images.forEach((image, index) => {
            if (image instanceof File) {
                formDataToSend.append(`images`, image); // Changed to simple array
            } else if (image.url) {
                formDataToSend.append(`existingImages`, image.url);
            }
        });
    
        // Handle deleted images (send IDs)
        deleteImages.forEach(imgId => {
            formDataToSend.append("delete_image", imgId);
        });
    
        // Handle deleted dishes (send IDs)
        deleteDishes.forEach(dishId => {
            formDataToSend.append("deleteDishes[]", dishId);
        });
    
        // Handle dishes - existing and new
        formData.dishes.forEach((dish, index) => {
            if (dish.id) {
                formDataToSend.append(`existing_dishes[${index}]`, dish.id);
            } else {
                formDataToSend.append(`dishes[]`, dish.name);
            }
        });
    
        // Handle category types - only send names (no deletion in your current form)
        formData.category_type.forEach((type, index) => {
            formDataToSend.append(`category_type[]`, type);
        });
    
        // Add all other form data
        Object.keys(formData).forEach(key => {
            if (key !== "banner_image" && 
                key !== "images" && 
                key !== "category_type" && 
                key !== "dishes") {
                formDataToSend.append(key, formData[key]);
            }
        });
    
        try {
            const response = await putApi(`/admin/category/${venueDetails.id}`, formDataToSend, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
    
            if (response.status === 200) {
                setEditModal(false);
                fetchVenues();
            }
        } catch (error) {
            console.error("Error updating venue:", error);
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className='detail-page-modal'>
            <div className='modal-header'>
                <h3>Edit Venue</h3>
                <button className='close-button' onClick={() => setEditModal(false)}>
                    <img src={window.getPublicUrl('/assets/images/Close_round_light.svg')} alt='close' />
                </button>
            </div>

            <div className='modal-body scrollable-modal-body'>
                <div className='input-main'>
                    <div className='row'>
                        <div className='col-sm-12'>
                            <div className='custom-floating-group'>
                                <input
                                    type="text"
                                    placeholder="Location"
                                    className="form-control"
                                    value={formData.location}
                                    // disabled
                                />
                                <label>Location</label>
                            </div>
                        </div>

                        {/* Disabled Map */}
                        <div className='col-sm-12 mb-4'>
                            {isLoaded && (
                                <GoogleMap
                                    mapContainerStyle={{ height: '400px', width: '100%' }}
                                    zoom={12}
                                    center={{
                                        lat: parseFloat(formData.latitude) || 40.73061,
                                        lng: parseFloat(formData.longitude) || -73.935242
                                    }}
                                    options={{
                                        // disableDefaultUI: true,
                                        draggable: false,
                                        zoomControl: false,
                                        scrollwheel: false,
                                        // disableDoubleClickZoom: true
                                    }}
                                >
                                    {formData.latitude && formData.longitude && (
                                        <Marker
                                            position={{
                                                lat: parseFloat(formData.latitude),
                                                lng: parseFloat(formData.longitude)
                                            }}
                                        />
                                    )}
                                </GoogleMap>
                            )}
                        </div>

                        {/* Basic Information Fields */}
                        {['name', 'website_link', 'phone_number', 'email', 'wishlist_booking_url', 
                          'short_desc', 'description' , 'category_type'].map(field => (
                            <div className='col-sm-6' key={field}>
                                <div className='custom-floating-group'>
                                    <input
                                        type={field.includes('email') ? 'email' : 'text'}
                                        name={field}
                                        placeholder=" "
                                        value={formData[field]}
                                        onChange={handleChange}
                                        disabled={field === 'category_type'}
                                    />
                                    <label>{field.replace('_', ' ')}</label>
                                    {errors[field] && (
                                        <span style={{ color: "red", fontSize: "14px" }}>
                                            {errors[field]}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}

                        <div className='col-sm-6'>
                            <div className='custom-floating-group price-range'>
                                <label>Price Range</label>
                                <div className="tabs">
                                    {['$', '$$', '$$$', '$$$$', '$$$$$'].map((priceOption, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`price-tab ${price === priceOption ? 'active' : ''}`}
                                            onClick={() => handlePriceRangeChange(priceOption)}
                                        >
                                            {priceOption}
                                        </button>
                                    ))}
                                </div>
                                {errors.price_range && (
                                    <span style={{ color: "red", fontSize: "14px" }}>
                                        {errors.price_range}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className='col-sm-6'>
                            <div className='custom-floating-group'>
                                <select name='type' value={formData.type} onChange={handleChange}>
                                    <option value=''>Select Venue Type</option>
                                    <option value='1'>Restaurant</option>
                                    <option value='2'>Bar</option>
                                    <option value='3'>Club</option>
                                    <option value='4'>City Attraction</option>
                                    <option value='5'>Hostel</option>
                                    <option value='6'>Experience</option>
                                </select>
                                <label>Type</label>
                                {errors.type && (
                                    <span style={{ color: "red", fontSize: "14px" }}>
                                        {errors.type}
                                    </span>
                                )}
                            </div>
                        </div>

           

                        {/* Dishes */}
                        {/* <div className='col-sm-12'>
                            <div className='custom-floating-group'>
                                <label>Dishes</label>
                                <div className="dish-tags">
                                    {formData.dishes.map((dish, index) => (
                                        <div key={index} className="dish-tag">
                                            {dish.name}
                                            <button 
                                                type="button" 
                                                className="remove-tag"
                                                onClick={() => handleDishRemove(dish.id)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div> */}

                        {/* Banner Image Upload */}
                        <div className='col-sm-6'>
                            <div className="upload-wrapper">
                                <input
                                    type="file"
                                    id="bannerImage"
                                    name="banner_image"
                                    className="upload-input"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="bannerImage" className="upload-label">
                                    <img className='upload-icon' src={window.getPublicUrl("/assets/images/upload.svg")} alt="" />
                                    Banner Image
                                    <div className='upload-box'>
                                        {errors.banner_image && (
                                            <span style={{ color: "red", fontSize: "14px" }}>
                                                {errors.banner_image}
                                            </span>
                                        )}
                                        {(filePreviews.banner_image || formData.banner_image) && (
                                            <div className='file-preview'>
                                                <img
                                                    src={filePreviews.banner_image || 
                                                        (typeof formData.banner_image === 'string' ? 
                                                            formData.banner_image : 
                                                            URL.createObjectURL(formData.banner_image))}
                                                    alt='Banner'
                                                />
                                                <button
                                                    className='cross'
                                                    onClick={removeBannerImage}
                                                >
                                                    <img src={window.getPublicUrl('/assets/images/gray-cross.svg')} alt='close' />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Additional Images Upload */}
                        <div className='col-sm-6'>
                            <div className="upload-wrapper">
                                <input
                                    type="file"
                                    id="images"
                                    name="images"
                                    className="upload-input"
                                    onChange={handleFileChange}
                                    multiple
                                />
                                <label htmlFor="images" className="upload-label">
                                    <img className='upload-icon' src={window.getPublicUrl("/assets/images/upload.svg")} alt="" />
                                    Images
                                    {errors.images && (
                                        <span style={{ color: "red", fontSize: "14px" }}>
                                            {errors.images}
                                        </span>
                                    )}
                                </label>
                            </div>
                        </div>

                        {/* Image Previews */}
                        <div className='col-sm-12 text-center'>
                            {filePreviews.images.map((preview, index) => (
                                <div className='file-preview' key={index}>
                                    <img
                                        src={preview.url || preview}
                                        alt={preview.url ? 'Existing' : 'New'}
                                    />
                                    <button className='cross' onClick={() => removeImage(index)}>
                                        <img src={window.getPublicUrl('/assets/images/gray-cross.svg')} alt='close' />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className='modal-footer'>
                <button className='cancel' onClick={() => setEditModal(false)}>Cancel</button>
                <button className='save' onClick={handleSubmit} disabled={loader}>
                    {loader && <div className='spinner-border' role='status'></div>}
                    Save
                </button>
            </div>
        </div>
    );
};

export default EditVenueModal;